import React, { useEffect, useState } from "react";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMPageLoader from "src/components/UI/atoms/ATMPageLoader/ATMPageLoader";
import {
  useGetAffidavitStampQuery,
  useUpdateAffidavitStampMutation,
  useUploadAffidavitStampMutation,
} from "src/services/AffidavitStampService";
import { showToast } from "src/utils/toaster/showToast";
import AffidavitTemplate from "src/pages/affidavit-certificate/AffidavitTemplate";
import {
  AffidavitMarks,
  AffidavitStamps,
  AffidavitStampSettings as Settings,
  DEFAULT_LAYOUT_ITEM,
  StampLayout,
  StampLayoutItem,
  StampLayoutKey,
  StampTextPosition,
  affidavitMarks,
  fullLayout,
  isNotaryOn,
  loadAffidavitStamps,
} from "src/pages/affidavit-certificate/affidavitStamps";

/**
 * Affidavit Stamp & Seal — the stamp tickets and notary seals printed on every
 * generated affidavit, and the serial number its "NOTED & REGISTERED" seal
 * counts up from. An image not uploaded is not printed.
 */

type Kind = "ticket10" | "ticket5" | "notary" | "noted" | "attested";

const IMAGES: { kind: Kind; field: keyof Settings; label: string; hint: string }[] = [
  {
    kind: "ticket10",
    field: "ticket10Image",
    label: "₹10 Ticket",
    hint: "Printed top left.",
  },
  {
    kind: "ticket5",
    field: "ticket5Image",
    label: "₹5 Ticket",
    hint: "Printed top right, twice side by side.",
  },
  {
    kind: "notary",
    field: "notarySealImage",
    label: "Notary Seal",
    hint: "Round seal stamped over each group of tickets. White background is removed.",
  },
  {
    kind: "noted",
    field: "notedSealImage",
    label: "Noted Seal (optional)",
    hint: "NOTED & REGISTERED with blank Date / Sr. No. lines. Without it the seal is drawn as text.",
  },
  {
    kind: "attested",
    field: "attestedSealImage",
    label: "Attested Seal",
    hint: "ATTESTED with name and sign, bottom centre. Its date is written on it.",
  },
];

const POSITIONS: { key: "notedDatePos" | "notedSerialPos" | "attestedDatePos"; label: string; needs: Kind }[] = [
  { key: "notedDatePos", label: "Date on noted seal", needs: "noted" },
  { key: "notedSerialPos", label: "Sr. No. on noted seal", needs: "noted" },
];

const LAYOUT_ITEMS: { key: StampLayoutKey; label: string; needs: Kind[] }[] = [
  { key: "ticket10", label: "₹10 ticket (left)", needs: ["ticket10"] },
  { key: "ticket5", label: "₹5 tickets (right)", needs: ["ticket5"] },
  { key: "notaryLeft", label: "Notary seal — left", needs: ["notary", "ticket10"] },
  { key: "notaryRight", label: "Notary seal — right", needs: ["notary", "ticket5"] },
  { key: "noted", label: "Noted & Registered", needs: [] },
  { key: "attested", label: "Attested seal", needs: ["attested"] },
];

const checkerboard: React.CSSProperties = {
  // so a transparent PNG is visibly transparent
  backgroundImage:
    "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
};

const inputClass =
  "w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-main";

const ImageCard = ({ settings, kind, field, label, hint }: { settings: Settings } & typeof IMAGES[number]) => {
  const [upload, uploadInfo] = useUploadAffidavitStampMutation();
  const [update, updateInfo] = useUpdateAffidavitStampMutation();
  const url = (settings[field] as string) || "";

  const handleUpload = (file?: File | null) => {
    if (!file) return;
    const body = new FormData();
    body.append("image", file);
    upload({ kind, body }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to upload.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  const handleRemove = () => {
    update({ removeImage: kind }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to remove.");
      } else {
        showToast("success", `${label} removed.`);
      }
    });
  };

  return (
    <div className="w-[200px] flex flex-col gap-2">
      <span className="block text-xs font-medium text-slate-600">{label}</span>
      <div
        className="h-[150px] border border-dashed border-slate-400 rounded flex items-center justify-center overflow-hidden"
        style={checkerboard}
      >
        {url ? (
          <img src={url} alt={label} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-xs text-slate-500 px-3 text-center">Not uploaded — not printed</span>
        )}
      </div>
      <label className="cursor-pointer text-center text-sm border border-primary-main text-primary-main rounded py-1.5 hover:bg-slate-50">
        {uploadInfo.isLoading ? "Uploading..." : url ? "Change Image" : "Upload Image"}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          disabled={uploadInfo.isLoading}
          onChange={(e) => {
            handleUpload(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
      {url && (
        <button
          type="button"
          className="text-xs text-red-600 underline"
          onClick={handleRemove}
          disabled={updateInfo.isLoading}
        >
          Remove image
        </button>
      )}
      <span className="text-[11px] text-slate-500 leading-snug">{hint}</span>
    </div>
  );
};

/** Notary ON / OFF: whether any of this goes on the affidavit at all. */
const NotaryCard = ({ settings }: { settings: Settings }) => {
  const [update, updateInfo] = useUpdateAffidavitStampMutation();
  const on = isNotaryOn(settings);

  const toggle = () => {
    update({ notaryEnabled: !on }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to save.");
      } else {
        showToast("success", `Notary turned ${on ? "OFF" : "ON"}.`);
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex items-center gap-4 flex-wrap">
      <div className="font-semibold text-primary-main text-base">Notary</div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={toggle}
        disabled={updateInfo.isLoading}
        className={`relative w-14 h-7 rounded-full transition-colors ${on ? "bg-green-600" : "bg-slate-400"} disabled:opacity-60`}
      >
        <span
          className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-7" : ""}`}
        />
      </button>
      <span className={`font-semibold ${on ? "text-green-700" : "text-slate-600"}`}>{on ? "ON" : "OFF"}</span>
      <span className="text-xs text-slate-600 flex-1 min-w-[260px]">
        {on
          ? "Tickets, seals and serial number are printed on the affidavit, and the PAN form (Fill / Refill) makes the affidavit and adds it as its last page."
          : "Plain affidavit, as before: no tickets, seals or serial number. The PAN form does not make or add the affidavit; generate it from the Affidavit Certificate screen."}
      </span>
    </div>
  );
};

const SerialCard = ({ settings }: { settings: Settings }) => {
  const [start, setStart] = useState("");
  const [update, updateInfo] = useUpdateAffidavitStampMutation();
  const yy = new Date().getFullYear().toString().slice(-2);

  const handleSave = () => {
    update({ serialStart: Number(start) }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to save.");
      } else {
        showToast("success", res?.data?.message);
        setStart("");
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-3">
      <div className="font-semibold text-primary-main text-base border-b pb-2">
        Serial Number (NOTED &amp; REGISTERED — At Sr. No.)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-600">Next number to be given</div>
          <div className="font-semibold text-lg">
            {settings.serialNext ? `${settings.serialNext}/${yy}` : "Not set"}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-600">Last used</div>
          <div className="font-semibold text-lg">
            {settings.lastUsedSerial ? `${settings.lastUsedSerial}` : "None yet"}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-600">Started from</div>
          <div className="font-semibold text-lg">{settings.serialStart || "—"}</div>
        </div>
      </div>
      <div className="flex gap-3 items-end flex-wrap">
        <label className="w-[220px]">
          <span className="block text-xs font-medium text-slate-600 mb-1">
            {settings.serialStart ? "Change start number" : "Start from number"}
          </span>
          <input
            type="text"
            inputMode="numeric"
            className={inputClass}
            value={start}
            placeholder="e.g. 37248"
            onChange={(e) => setStart(e.target.value.replace(/\D/g, "").slice(0, 8))}
          />
        </label>
        <ATMLoadingButton
          className="bg-primary-main w-auto px-6"
          onClick={handleSave}
          isLoading={updateInfo.isLoading}
          disabled={!start || updateInfo.isLoading}
        >
          Save
        </ATMLoadingButton>
      </div>
      <span className="text-[11px] text-slate-500 leading-snug">
        Each application gets the next number the first time its affidavit is
        generated, and keeps it on refill / update. The start can be any number
        that is not already used; numbers already given out are skipped. The year after
        "/" and the date (the day before generation) are added automatically.
        Without a start number, NOTED &amp; REGISTERED is not printed.
      </span>
    </div>
  );
};

type Positions = Record<string, StampTextPosition | undefined>;

/** Sliders for where the date / serial are written; `values` also drive the preview. */
const PositionCard = ({
  settings,
  values,
  setValues,
}: {
  settings: Settings;
  values: Positions;
  setValues: React.Dispatch<React.SetStateAction<Positions>>;
}) => {
  const [update, updateInfo] = useUpdateAffidavitStampMutation();

  const has: Record<Kind, boolean> = {
    ticket10: !!settings.ticket10Image,
    ticket5: !!settings.ticket5Image,
    notary: !!settings.notarySealImage,
    noted: !!settings.notedSealImage,
    attested: !!settings.attestedSealImage,
  };
  const shown = POSITIONS.filter((p) => has[p.needs]);
  if (!shown.length) return null;

  const set = (key: string, part: keyof StampTextPosition, value: number) =>
    setValues((prev) => ({
      ...prev,
      [key]: { x: 0, y: 0, fontSize: 16, ...prev[key], [part]: value },
    }));

  const handleSave = () => {
    update(values).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to save.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-3">
      <div className="font-semibold text-primary-main text-base border-b pb-2">
        Text position on the seals
      </div>
      <div className="flex flex-wrap gap-6">
        {shown.map(({ key, label }) => {
          const pos = values[key] || { x: 0, y: 0, fontSize: 16 };
          return (
            <div key={key} className="w-[240px] flex flex-col gap-1 text-xs">
              <span className="font-medium text-slate-700">{label}</span>
              {(["x", "y", "fontSize"] as const).map((part) => (
                <label key={part} className="flex items-center gap-2">
                  <span className="w-[60px] text-slate-600">
                    {part === "x" ? "Across %" : part === "y" ? "Down %" : "Size px"}
                  </span>
                  <input
                    type="range"
                    min={part === "fontSize" ? 8 : 0}
                    max={part === "fontSize" ? 40 : 100}
                    value={pos[part]}
                    onChange={(e) => set(key, part, Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="w-[28px] text-right">{pos[part]}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end">
        <ATMLoadingButton
          className="bg-primary-main w-auto px-6"
          onClick={handleSave}
          isLoading={updateInfo.isLoading}
          disabled={updateInfo.isLoading}
        >
          Save positions
        </ATMLoadingButton>
      </div>
      <span className="text-[11px] text-slate-500 leading-snug">
        The preview follows the sliders; Save positions to use them on affidavits.
      </span>
    </div>
  );
};

/** Size / position sliders for every ticket and seal; `layout` also drives the preview. */
const LayoutCard = ({
  settings,
  layout,
  setLayout,
}: {
  settings: Settings;
  layout: StampLayout;
  setLayout: React.Dispatch<React.SetStateAction<StampLayout>>;
}) => {
  const [update, updateInfo] = useUpdateAffidavitStampMutation();

  const has: Record<Kind, boolean> = {
    ticket10: !!settings.ticket10Image,
    ticket5: !!settings.ticket5Image,
    notary: !!settings.notarySealImage,
    noted: !!settings.notedSealImage,
    attested: !!settings.attestedSealImage,
  };
  // Noted & Registered prints (as image or as text) whenever a serial is set.
  const shown = LAYOUT_ITEMS.filter((item) =>
    item.key === "noted" ? !!settings.serialNext : item.needs.every((kind) => has[kind])
  );
  if (!shown.length) return null;

  const set = (key: StampLayoutKey, part: keyof StampLayoutItem, value: number) =>
    setLayout((prev) => ({ ...prev, [key]: { ...prev[key], [part]: value } }));

  const handleSave = () => {
    update({ layout }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to save.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  const SLIDERS: { part: keyof StampLayoutItem; label: string; min: number; max: number; unit: string }[] = [
    { part: "size", label: "Size", min: 50, max: 200, unit: "%" },
    { part: "x", label: "Left ↔ Right", min: -60, max: 60, unit: "mm" },
    { part: "y", label: "Up ↕ Down", min: -60, max: 60, unit: "mm" },
  ];

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-3">
      <div className="font-semibold text-primary-main text-base border-b pb-2">
        Size &amp; position of tickets and seals
      </div>
      <div className="flex flex-wrap gap-6">
        {shown.map(({ key, label }) => {
          const item = layout[key] || DEFAULT_LAYOUT_ITEM;
          return (
            <div key={key} className="w-[260px] flex flex-col gap-1 text-xs">
              <div className="flex justify-between items-center">
                <span className="font-medium text-slate-700">{label}</span>
                <button
                  type="button"
                  className="text-[11px] text-primary-main underline"
                  onClick={() => setLayout((prev) => ({ ...prev, [key]: { ...DEFAULT_LAYOUT_ITEM } }))}
                >
                  Reset
                </button>
              </div>
              {SLIDERS.map(({ part, label: partLabel, min, max, unit }) => (
                <label key={part} className="flex items-center gap-2">
                  <span className="w-[72px] text-slate-600">{partLabel}</span>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    value={item[part]}
                    onChange={(e) => set(key, part, Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="w-[44px] text-right">
                    {item[part]}
                    {unit}
                  </span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end">
        <ATMLoadingButton
          className="bg-primary-main w-auto px-6"
          onClick={handleSave}
          isLoading={updateInfo.isLoading}
          disabled={updateInfo.isLoading}
        >
          Save size &amp; position
        </ATMLoadingButton>
      </div>
      <span className="text-[11px] text-slate-500 leading-snug">
        Moved from the default place: 0 mm and 100% is the default. The preview
        follows the sliders; Save to use them on affidavits.
      </span>
    </div>
  );
};

const SAMPLE = {
  applicantName: "SAMPLE APPLICANT NAME",
  fatherName: "SAMPLE FATHER NAME",
  dob: "01/01/2000",
  address: "ROOM NO 1, SAMPLE NAGAR, THANE",
  place: "THANE",
};

const Preview = ({
  settings,
  positions,
  layout,
}: {
  settings: Settings;
  positions: Positions;
  layout: StampLayout;
}) => {
  const [stamps, setStamps] = useState<AffidavitStamps | null>(null);
  const [marks, setMarks] = useState<AffidavitMarks | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadAffidavitStamps(settings).then((loaded) => {
      if (!cancelled) setStamps(loaded);
    });
    const noted = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, "0");
    // Notary OFF: the plain affidavit, without a serial.
    const number = isNotaryOn(settings) ? settings.serialNext || 0 : 0;
    affidavitMarks(
      number
        ? {
            serialNumber: number,
            serialLabel: `${number}/${String(noted.getFullYear()).slice(-2)}`,
            notedDate: `${noted.getFullYear()}-${pad(noted.getMonth() + 1)}-${pad(noted.getDate())}`,
          }
        : null
    ).then((m) => {
      if (!cancelled) setMarks(m);
    });
    return () => {
      cancelled = true;
    };
  }, [settings]);

  if (!stamps) return <ATMPageLoader />;
  const live: AffidavitStamps = {
    ...stamps,
    notedDatePos: positions.notedDatePos || stamps.notedDatePos,
    notedSerialPos: positions.notedSerialPos || stamps.notedSerialPos,
    attestedDatePos: positions.attestedDatePos || stamps.attestedDatePos,
    layout,
  };
  return (
    // A4 at 60%: 210mm ≈ 794px → 476px wide.
    <div style={{ width: "480px", height: "680px", overflow: "hidden", border: "1px solid #ccc" }}>
      <div style={{ transform: "scale(0.6)", transformOrigin: "top left", width: "794px" }}>
        <AffidavitTemplate
          {...SAMPLE}
          todayDate="1 January 2026"
          acknowledgementNumber="000000123456"
          stamps={live}
          marks={marks}
        />
      </div>
    </div>
  );
};

const AffidavitStampSettings = () => {
  const { data, isLoading } = useGetAffidavitStampQuery(undefined);
  const settings: Settings | undefined = data?.data;
  // Slider values, shared with the preview before they are saved.
  const [positions, setPositions] = useState<Positions>({});
  const [layout, setLayout] = useState<StampLayout>(() => fullLayout());

  useEffect(() => {
    if (!settings) return;
    setPositions({
      notedDatePos: settings.notedDatePos,
      notedSerialPos: settings.notedSerialPos,
      attestedDatePos: settings.attestedDatePos,
    });
    setLayout(fullLayout(settings.layout));
  }, [settings]);

  return (
    <SideNavLayout>
      <div className="p-4 flex flex-col gap-4 h-full overflow-auto">
        <div>
          <div className="text-xl font-medium">Affidavit Stamp &amp; Seal</div>
          <div className="text-sm text-slate-600">
            Stamp tickets, notary seals and serial number printed on every
            generated affidavit (Affidavit Certificate screen and PAN form).
          </div>
        </div>
        {isLoading || !settings ? (
          <ATMPageLoader />
        ) : (
          <div className="flex gap-4 flex-wrap items-start">
            <div className="flex-1 min-w-[520px] flex flex-col gap-4">
              <NotaryCard settings={settings} />
              <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-3">
                <div className="font-semibold text-primary-main text-base border-b pb-2">
                  Tickets &amp; Seals
                </div>
                <div className="flex gap-5 flex-wrap">
                  {IMAGES.map((image) => (
                    <ImageCard key={image.kind} settings={settings} {...image} />
                  ))}
                </div>
              </div>
              <SerialCard settings={settings} />
              <LayoutCard settings={settings} layout={layout} setLayout={setLayout} />
              <PositionCard
                settings={settings}
                values={positions}
                setValues={setPositions}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-slate-700">Preview (sample details)</div>
              <Preview settings={settings} positions={positions} layout={layout} />
            </div>
          </div>
        )}
      </div>
    </SideNavLayout>
  );
};

export default AffidavitStampSettings;
